import "@fontsource/lato/latin-400.css"
import "@fontsource/lato/latin-400-italic.css"
import "@fontsource/lato/latin-700.css"
import "@fontsource/lato/latin-900.css"

import type { ReactNode } from "react"
import { useRef, useState } from "react"
import { format } from "date-fns"
import {
	IconCaretDownFill18 as CaretDown,
	IconMediaPlayFill18 as Play,
} from "nucleo-ui-fill-18"

import type {
	SlackChangelogBlock,
	SlackTextObject,
} from "@productlane/api/src/lib/slack/changelog-message"

import { SlackMrkdwn } from "./slack-mrkdwn"

function SlackText({ text }: { text: SlackTextObject }) {
	return text.type === "mrkdwn" ? (
		<SlackMrkdwn text={text.text} />
	) : (
		<>{text.text}</>
	)
}

function SlackBlockView({ block }: { block: SlackChangelogBlock }) {
	switch (block.type) {
		case "header":
			return (
				<div className="text-[16px] font-bold leading-[24px]">
					{block.text.text}
				</div>
			)
		case "context":
			return (
				<div className="flex flex-wrap gap-2 text-[13px] leading-[18px] text-[#616061] dark:text-[#ABABAD]">
					{block.elements.map((element, i) => (
						<span key={i}>
							<SlackText text={element} />
						</span>
					))}
				</div>
			)
		case "image":
			return (
				<img
					src={block.image_url}
					alt={block.alt_text}
					className="max-h-[360px] w-full max-w-[480px] rounded-lg border border-[#1D1C1D]/[0.13] object-contain dark:border-[#E8E8E8]/[0.13]"
				/>
			)
		case "section":
			return (
				<div className="text-[15px] leading-[22px]">
					<SlackText text={block.text} />
				</div>
			)
		case "actions":
			return (
				<div className="flex flex-wrap gap-2">
					{block.elements.map((element, i) => (
						// A replica of Slack's own button, so the preview reads as the post; it opens the real link.
						<a
							key={i}
							href={element.url}
							target="_blank"
							rel="noreferrer noopener"
							className="inline-flex h-7 items-center rounded border border-[#1D1C1D]/30 bg-white px-3 text-[13px] font-bold text-[#1D1C1D] hover:bg-[#F8F8F8] dark:border-[#E8E8E8]/30 dark:bg-transparent dark:text-[#D1D2D3] dark:hover:bg-[#E8E8E8]/[0.04]"
						>
							{element.text.text}
						</a>
					))}
				</div>
			)
	}
}

// Slack's message metrics: Lato 15/22 body, bold 15px names, 12px timestamps.
function SlackMessage({
	name,
	iconUrl,
	children,
}: {
	name: string
	iconUrl: string
	children: ReactNode
}) {
	return (
		<div className="flex gap-2">
			<img
				src={iconUrl}
				alt=""
				className="mt-[3px] h-9 w-9 flex-none rounded-lg object-cover"
			/>
			<div className="flex min-w-0 flex-1 flex-col">
				<div className="flex items-baseline gap-2 leading-[22px]">
					<span className="truncate text-[15px] font-bold">{name}</span>
					<span className="flex-none self-center rounded-[2px] bg-[#1D1C1D]/[0.13] px-[3px] text-[10px] font-bold leading-[14px] text-[#1D1C1D]/70 dark:bg-[#E8E8E8]/[0.13] dark:text-[#E8E8E8]/70">
						APP
					</span>
					<span className="flex-none text-[12px] text-[#616061] dark:text-[#ABABAD]">
						{format(new Date(), "h:mm a")}
					</span>
				</div>
				{children}
			</div>
		</div>
	)
}

// Slack shows an uploaded video as its first frame with a "Video" pill, then plays inline.
function SlackVideo({ url, title }: { url: string; title: string }) {
	const videoRef = useRef<HTMLVideoElement>(null)
	const [started, setStarted] = useState(false)
	return (
		<div className="flex flex-col gap-1">
			<span className="flex items-center gap-1 text-[15px] leading-[22px] text-[#616061] dark:text-[#ABABAD]">
				<span className="truncate">{title}</span>
				<CaretDown className="h-3 w-3 flex-none" aria-hidden />
			</span>
			<div className="relative w-full max-w-[420px] overflow-hidden rounded-lg border border-[#1D1C1D]/[0.13] bg-black dark:border-[#E8E8E8]/[0.13]">
				<video
					ref={videoRef}
					src={url}
					controls={started}
					preload="metadata"
					onPlay={() => setStarted(true)}
					className="block aspect-video w-full object-contain"
				>
					<track kind="captions" />
				</video>
				{!started && (
					<button
						type="button"
						onClick={() => {
							setStarted(true)
							void videoRef.current?.play().catch(() => undefined)
						}}
						className="absolute inset-0 flex items-end p-3"
						aria-label={`Play ${title}`}
					>
						<span className="flex items-center gap-2 rounded-md bg-black/75 px-3 py-1.5 text-[15px] font-bold text-white">
							<Play className="h-3.5 w-3.5 flex-none" aria-hidden />
							Video
						</span>
					</button>
				)}
			</div>
		</div>
	)
}

/** The changelog post as Slack lays it out, drawn from the blocks the broadcast sends. */
export function SlackMessagePreview({
	username,
	iconUrl,
	blocks,
	video,
}: {
	username: string
	iconUrl: string
	blocks: SlackChangelogBlock[]
	video: { url: string; title: string } | null
}) {
	// Slack's light and dark palettes, sitting flat on the preview pane in both themes.
	return (
		<div className="flex flex-col gap-4 bg-white font-['Lato',Helvetica,Arial,sans-serif] text-[#1D1C1D] dark:bg-transparent dark:text-[#D1D2D3]">
			<SlackMessage name={username} iconUrl={iconUrl}>
				<div className="flex flex-col gap-2">
					{blocks.map((block, i) => (
						<SlackBlockView key={i} block={block} />
					))}
					{/* The video file rides on the same message, under its blocks. */}
					{video && <SlackVideo url={video.url} title={video.title} />}
				</div>
			</SlackMessage>
		</div>
	)
}
